import { useEffect, useRef, useState } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { useTranslation } from '@/hooks/useTranslation';
import styles from './RouteProgressBar.module.scss';

/**
 * Top-of-viewport progress bar shown during TanStack Router navigation
 * transitions. Provides visual feedback when route lazy chunks (and any
 * route-level loaders) are in flight.
 *
 * Animation profile (NProgress-style fake progress):
 *   t=0    8%      kick-off, visible immediately
 *   t=80   28%     fast initial burst — "click registered"
 *   t=220  55%     mid-progress
 *   t=500  78%     decelerating
 *   t=1200 90%     crawl
 *   t=2500 94%     tail — never reaches 95% on its own
 *   on settle      snap to 100% → 180ms hold → 280ms fade → unmount
 *
 * The asymptotic crawl below 95% is deliberate: if the bar hit 100% before
 * the route actually settled, users would expect content imminently and
 * perceive the actual settle as a regression. Crawling near 95% sets the
 * expectation "almost there" without ever lying about completion.
 */
export default function RouteProgressBar() {
  const { t } = useTranslation();
  const isLoading = useRouterState({
    select: (s) => s.isLoading || s.isTransitioning,
  });

  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const [fading, setFading] = useState(false);
  const wasLoadingRef = useRef(false);
  const timersRef = useRef<number[]>([]);

  useEffect(() => {
    const clearTimers = () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };

    if (isLoading) {
      clearTimers();
      wasLoadingRef.current = true;
      setVisible(true);
      setFading(false);
      setProgress(8);

      const stages: Array<{ target: number; after: number }> = [
        { target: 28, after: 80 },
        { target: 55, after: 220 },
        { target: 78, after: 500 },
        { target: 90, after: 1200 },
        { target: 94, after: 2500 },
      ];
      stages.forEach(({ target, after }) => {
        timersRef.current.push(window.setTimeout(() => setProgress(target), after));
      });
    } else if (wasLoadingRef.current) {
      // Loading finished — snap to 100, hold briefly so users register
      // completion, then fade the whole bar and unmount.
      clearTimers();
      wasLoadingRef.current = false;
      setProgress(100);

      timersRef.current.push(window.setTimeout(() => setFading(true), 180));
      timersRef.current.push(
        window.setTimeout(() => {
          setVisible(false);
          setFading(false);
          setProgress(0);
        }, 480),
      );
    }

    return clearTimers;
  }, [isLoading]);

  if (!visible) return null;

  const rootClass = fading ? `${styles.root} ${styles.fading}` : styles.root;

  return (
    <div
      className={rootClass}
      role="progressbar"
      aria-label={t('K_ROUTE_LOADING', 'Loading next page')}
      aria-valuetext={t('K_ROUTE_LOADING', 'Loading next page')}
    >
      <div
        className={styles.bar}
        style={{ transform: `scaleX(${progress / 100})` }}
      />
    </div>
  );
}
